import { Injectable, isDevMode } from '@angular/core';
import { CommentModel } from '@barthauer/abc/common-editors';
import { CompareHelper, ConvertHelper } from '@barthauer/abc/core';
import { LoginInfo, UserService } from '@barthauer/abc/core/authentication';
import { AbcConfigService } from '@barthauer/abc/core/configuration';
import {
  CacheStorageAdapter,
  DataService as CoreDataService,
  NotificationData,
  PendingObjectsChangedPackage,
  PersistenceService,
  Query,
  QueryColumn,
  QueryColumnSortOrder,
  QueryObjectsModel,
  QueryValueModel,
  ReadListDataSourceModel,
  ReadListDataSourceResult,
  ReadPicklistItemsModel,
  SendNotificationModel,
} from '@barthauer/abc/core/data';
import { ErrorHandlingService, ErrorInfo } from '@barthauer/abc/core/error-handling';
import { DataEntity, MapService } from '@barthauer/abc/graphics/adapters/open-layers';
import { DialogResult, MessageBoxService, MessageMode, MessageSettings } from '@barthauer/abc/messages';
import { ImWorkService } from '@barthauer/basys/opera/wizards';
import { AfterOfflineStorageAction } from 'libs/abc/core/data/src/lib/caching/cache-persistence';
import { MtWorkType } from 'libs/basys/opera/wizards/src/lib/create-works-wizard/models/MtWorkType';
import { WorkService } from 'libs/basys/opera/wizards/src/lib/create-works-wizard/services/work.service';
import { forkJoin, from, Observable, of, Subscription } from 'rxjs';
import { concatMap, first, map } from 'rxjs/operators';
import { MtObject, MtPositionType, MtResultPosType, MtResultType, MtWork, Order } from '../models/mtData.model';
import { MtResourceType, MtResource } from '../models/mtData.resources.model';
import { MtTicket, MtTicketCounterHelper } from '../models/mtData.tickets.model';
import { IDrawable } from '../viewModels/mobileData.viewModel';
import { WorkState } from '../works/works.model';
import { ParameterValue } from '@barthauer/abc/queries/parameters/core';
import { OperaContext } from '@barthauer/abc/maintenance/opera/core';

@Injectable({
  providedIn: 'root',
  deps: [PersistenceService],
})
export class DataService {
  public static readonly MainObjectSpaceName: string = 'BaSYS.Complete';
  public static readonly COL_CountTicketsAll: string = 'CountTicketsAll';
  public static readonly COL_CountTicketsFinished: string = 'CountTicketsFinished';
  public static readonly COL_CountTicketsEditing: string = 'CountTicketsEditing';
  public static readonly COL_CountTicketsNotStarted: string = 'CountTicketsNotStarted';
  public static readonly COL_HasTicketTaskReminders: string = 'HasTicketTaskReminders';
  public static readonly COMMENT_IDENTIFIER_NO_FOTO: string = 'NoPhoto';
  public static readonly COMMENT_IDENTIFIER_NOTE: string = 'Bemerkung';
  public static readonly COMMENT_NO_PHOTO_IS_POSSIBLE: string = 'No photo is possible';
  public static readonly COMMENT_NO_PHOTO_IS_POSSIBLE_OPATH: string =
    "Comments[SeqNr=1 And Identifier='" + DataService.COMMENT_IDENTIFIER_NO_FOTO + "'].Text";
  public static readonly COMMENT_NOTE_OPATH: string =
    "Comments[SeqNr=1 And Identifier='" + DataService.COMMENT_IDENTIFIER_NOTE + "'].Text";
  public getOrderId: () => Promise<string>;
  public isReadOnly: boolean;
  public pendingObjectsChanged$: Observable<PendingObjectsChangedPackage>;
  private stateToPersist: any;
  public refreshState: () => void;

  public setStateToPersist(state: any) {
    this.stateToPersist = state;
    this.persistenceService.cacheAdapter.saveState(this.lastStateContext, this.stateToPersist).then(() => {});
  }

  private static readonly stateStorageName = 'opera.works.current';
  private static readonly lastStateStorageName = 'opera.works.last';
  private get currentStateContext(): string {
    return DataService.stateStorageName;
  }
  public get lastStateContext(): string {
    return DataService.lastStateStorageName;
  }

  private subscriptions: Subscription[] = [];
  public notify: (msg: string) => void = alert;
  public notifyError: (msg: string) => void = alert;
  public cacheRefreshed: (state: any) => void;

  constructor(
    private persistenceService: PersistenceService,
    private mapService: MapService,
    private coreDataService: CoreDataService,
    private userService: UserService,
    private messageBoxService: MessageBoxService,
    private error: ErrorHandlingService,
    private configService: AbcConfigService,
  ) {
    this.pendingObjectsChanged$ = persistenceService.pendingObjectsChanged$;

    this.notifyError = (msg) => this.error.publish(msg);

    this.subscriptions.push(
      this.persistenceService.cacheAdapter.storedOnline.subscribe(async (_) => {
        for (const context of Object.keys(_.contexts)) {
          await _.adapter.saveState(context, null);
        }
        if (_.results?.length) {
          const entities = this.getEntitiesFromState(this.stateToPersist, _.results);
          if (entities) {
            this.mapService.setCustomColor(null, null);
            this.redrawEntities(entities.map((_) => _.entity));
          }

          if (this.refreshState) {
            this.refreshState();
          }
          if (_.results?.length === 1) {
            this.notify('Es wurde eine temporär gespeicherte Aktion online ausgeführt!');
          } else {
            this.notify('Es wurden ' + _.results?.length + ' temporär gespeicherte Aktionen online ausgeführt!');
          }
        }
      }),
    );

    this.subscriptions.push(
      persistenceService.afterPersistPendingChanges$
        .pipe(
          map((persistedObjects) => {
            if (!(persistedObjects instanceof ErrorInfo)) {
              const entities: DataEntity[] = persistedObjects
                .filter((obj) => (obj as unknown as IDrawable).drawableObjectId)
                .map((obj) => {
                  const drawObj = obj as unknown as IDrawable;
                  return {
                    id: drawObj.drawableObjectId,
                    objectType: drawObj.drawableObjectType,
                    objectTypeName: '',
                  };
                });
              this.redrawEntities(entities);
            } else {
              const err = persistedObjects as ErrorInfo;
              if (!err.isWarning && !CacheStorageAdapter.useOfflineMode && this.configService.isAppLike) {
                return err;
              } else if (!err.isWarning) {
                this.notifyError(err.getFullMessage());
              }
            }
            return null;
          }),
          concatMap((err) => (err ? this.confirmSyncCaching(err) : of(DialogResult.Cancel))),
          concatMap((result: DialogResult) => {
            if (DialogResult.Yes === result['result']) {
              return from(this.refreshCache());
            } else {
              return of(0);
            }
          }),
        )
        .subscribe((count) => {
          if (count) {
            this.notify('Inkonsistente Offline-Daten bereinigt!');
          }
        }),
    );
  }

  private getEntitiesFromState(state: any, results: any[]): { entity: DataEntity; works: MtWork[] }[] {
    const objects = state?.mtObjects as MtObject[];
    if (Array.isArray(objects)) {
      const ids: string[] = [];
      this.addIdsFromResults(ids, results);
      const entities: { entity: DataEntity; works: MtWork[] }[] = [];
      for (const o of objects) {
        for (const id of ids) {
          if (
            o.GeObjectId === id ||
            o.Works?.find(
              (w) => w.Id === id || w.Tasks?.find((t) => t.Id === id || t.Results?.find((r) => r.Id === id)),
            )
          ) {
            entities.push({ entity: new DataEntity(o.GeObjectType, o.GeObjectId), works: o.Works });
            break;
          }
        }
      }
      return entities;
    }
  }

  private addIdsFromResults(ids: string[], results: any[]) {
    for (const result of results) {
      if (Array.isArray(result)) {
        this.addIdsFromResults(ids, result);
      } else {
        let id: string = result?.Id;
        if (!id) {
          const columns = result?.TargetColumns;
          if (columns) {
            for (const c of columns) {
              if (c.Name?.toLowerCase() === 'id' && c.Value) {
                id = c.Value;
                break;
              }
            }
          }
        }
        if (!id) {
          id = result?.ObjectKey?.Id;
        }
        if (id) {
          ids.push(id);
        }
      }
    }
  }

  public redrawEntities(entities: DataEntity[]) {
    if (entities && entities.length > 0) {
      CacheStorageAdapter.forceOnlineMode(30);
      if (isDevMode()) {
        console.log('redrawing objects:');
        console.log(entities);
      }
      this.mapService.refreshMapLayer(entities);
    }
  }

  async refreshCache(): Promise<number> {
    const contexts: any = {};
    const count = await this.persistenceService.cacheAdapter.deleteStoredQueries(contexts);
    for (const context of Object.keys(contexts)) {
      await this.persistenceService.cacheAdapter.saveState(context, null);
    }
    if (count) {
      this.stateToPersist = null;
      if (this.refreshState) {
        this.refreshState();
      }

      if (this.cacheRefreshed) {
        this.cacheRefreshed(this.persistenceService.cacheAdapter.loadState(this.currentStateContext));
      }
    }
    return count;
  }

  private confirmSyncCaching(err: ErrorInfo): Observable<DialogResult> {
    const settings = new MessageSettings();
    settings.mode = MessageMode.Question;
    settings.message =
      'Inkonsistente Offline-Daten oder sonstigen Fehler festgestellt. Sollen die Daten synchronisiert werden?';
    const errorMessage = err?.message;
    if (errorMessage) {
      settings.message += '\n\n' + errorMessage.substring(0, 200);
    }
    return this.messageBoxService.showMessageBox(settings);
  }

  persistPendingChanges(category?: string, onerror?: (err) => void) {
    if (!this.isReadOnly) {
      this.persistenceService.persistPendingChanges(category, this.getAcceptOfflineStorage(), onerror);
    }
  }

  persistPendingChangesAsync(category?: string): Promise<void> {
    if (!this.isReadOnly) {
      return this.persistenceService.persistPendingChangesAsync(category, this.getAcceptOfflineStorage());
    }
  }

  getAcceptOfflineStorage(): () => AfterOfflineStorageAction {
    let acceptOfflineStorage: () => AfterOfflineStorageAction;
    if (this.stateToPersist) {
      const currentContext = this.currentStateContext;
      acceptOfflineStorage = () => {
        return {
          context: currentContext,
          action: async (adapter, context, data) => {
            if (context == currentContext) {
              await adapter.saveState(currentContext, this.stateToPersist);
              const entities = this.getEntitiesFromState(this.stateToPersist, data);
              if (entities) {
                const finishedEntities: DataEntity[] = [];
                const partiallyfinishedEntities: DataEntity[] = [];
                const otherEntities: DataEntity[] = [];
                for (const e of entities) {
                  if (e.works?.find((_) => _.State === WorkState.Erledigt)) {
                    finishedEntities.push(e.entity);
                  } else if (e.works?.find((_) => _.State === WorkState.Teilerledigt)) {
                    partiallyfinishedEntities.push(e.entity);
                  } else {
                    otherEntities.push(e.entity);
                  }
                }
                this.mapService.setCustomColor(finishedEntities, [0, 255, 255]);
                this.mapService.setCustomColor(partiallyfinishedEntities, [255, 0, 255]);
                this.mapService.setCustomColor(otherEntities, [192, 192, 192]);
              }
            }
          },
        };
      };
    }
    return acceptOfflineStorage;
  }

  revertPendingChanges(category?: string) {
    this.persistenceService.revertPendingChanges(category);
  }

  getMtObjects(refreshCache?: boolean, workIds?: string[], indexedMtPositionTypes?: {}): Observable<MtObject[]> {
    return from(this.createMtObjectsQueryAsync(workIds, indexedMtPositionTypes)).pipe(
      concatMap((query) => {
        return this.coreDataService.executeReadObjectsQuery(query, false, refreshCache).pipe(
          map((data) => (!CompareHelper.isEmptyObject(data) ? (data as any as object[]) : [])),
          map((data) => data.map((obj) => new MtObject(obj))),
        );
      }),
    );
  }

  getMtResourceTypes(refreshCache?: boolean): Observable<MtResourceType[]> {
    return from(this.createMtResourceTypesQuery()).pipe(
      concatMap((query) => {
        return this.coreDataService.executeReadObjectsQuery(query, false, refreshCache).pipe(
          map((data) => (!CompareHelper.isEmptyObject(data) ? (data as any as object[]) : [])),
          map((data) => data.map((obj) => new MtResourceType(obj))),
        );
      }),
    );
  }

  getMtWorkTypes(refreshCache?: boolean): Observable<MtWorkType[]> {
    return from(this.createMtWorkTypesQuery()).pipe(
      concatMap((query) => {
        return this.coreDataService.executeReadObjectsQuery(query, false, refreshCache).pipe(
          map((data) => (!CompareHelper.isEmptyObject(data) ? (data as any as object[]) : [])),
          map((data) => data.map((obj) => <MtWorkType>obj)),
        );
      }),
    );
  }

  getMtTickets(refreshCache?: boolean): Observable<MtTicket[]> {
    return from(this.createMtTicketsQueryAsync()).pipe(
      concatMap((query) => {
        return this.coreDataService.executeReadObjectsQuery(query, false, refreshCache).pipe(
          map((data) => (!CompareHelper.isEmptyObject(data) ? (data as any as object[]) : [])),
          map((data) => data.map((obj) => new MtTicket(obj))),
        );
      }),
    );
  }

  getMtTicketResources(refreshCache?: boolean): Observable<MtResource[]> {
    return from(this.createMtTicketResourcesQueryAsync()).pipe(
      concatMap((query) => {
        return this.coreDataService.executeReadObjectsQuery(query, false, refreshCache).pipe(
          map((data) => (!CompareHelper.isEmptyObject(data) ? (data as any as object[]) : [])),
          map((data) => data.map((obj) => new MtResource(obj))),
        );
      }),
    );
  }

  getMtTicketsCountHelper(): Observable<MtTicketCounterHelper> {
    return from(this.createMtTicketsCountQueryAsync()).pipe(
      concatMap((query) => {
        return this.coreDataService.executeReadObjectsQuery(query).pipe(
          map((data) => (!CompareHelper.isEmptyObject(data) ? (data as any as object[]) : [])),
          map((data) => data.map((obj) => new MtTicketCounterHelper(obj))),
          map((array) => array[0]),
          first(),
        );
      }),
    );
  }

  getMtTicketsCount(): Observable<number> {
    return this.getMtTickets().pipe(
      concatMap((tickets) => {
        return of(tickets.length);
      }),
    );
  }

  hasMtTickets(): Observable<boolean> {
    return from(this.hasMtTicketsCore());
  }

  public async hasMtTicketReminders(): Promise<boolean> {
    let retHas: boolean = false;
    const hasTickets: boolean = await this.hasMtTickets().toPromise();
    if (hasTickets) {
      const counter = await this.getMtTicketsCountHelper().toPromise();
      retHas = counter?.HasTicketTaskReminders;
    }

    return retHas;
  }

  async hasMtResources(): Promise<boolean> {
    const query: QueryValueModel = new QueryValueModel();
    query.ObjectSpaceName = OperaContext.objectSpaceName;
    query.OPath = 'Exists(Type(MtResource)[Type=1].ResourceOrders[ParentId=$OrderId])';
    query.Parameters = [new ParameterValue('OrderId', await this.getOrderId())];

    return this.coreDataService
      .executeReadValueQuery(query)
      .pipe(
        map((data) => (!CompareHelper.isEmptyObject(data) ? (data as any) : '0')),
        map((data) => ConvertHelper.toBoolean(data)),
      )
      .toPromise();
  }

  async hasMtWorkTypes(): Promise<boolean> {
    const query: QueryValueModel = new QueryValueModel();
    query.ObjectSpaceName = OperaContext.objectSpaceName;
    query.OPath =
      'Exists(Type(MtWorkType)[' + (await this.getWorkTypeFilter(ImWorkService.AdditionalWorkTypeFilter)) + '])';

    return this.coreDataService
      .executeReadValueQuery(query)
      .pipe(
        map((data) => (!CompareHelper.isEmptyObject(data) ? (data as any) : '0')),
        map((data) => ConvertHelper.toBoolean(data)),
      )
      .toPromise();
  }

  private hasTicketLicense: boolean = undefined;
  private async hasMtTicketsCore(): Promise<boolean> {
    if (this.hasTicketLicense === undefined) {
      this.hasTicketLicense = await this.isTicketLicensed();
    }

    if (!this.hasTicketLicense) {
      return false;
    } else {
      const counter = await this.getMtTicketsCountHelper().toPromise();
      return counter?.CountTicketsAll > 0;
    }
  }

  public async isTicketLicensed(): Promise<boolean> {
    let isLicensed: boolean = undefined;
    try {
      const licenses = ConvertHelper.arrayBufferToObjectArray(
        await this.coreDataService
          .checkLicenses({
            Features: [
              { AppIdentifier: this.configService.configuration.ApplicationIdentifier, FeatureName: 'Tickets' },
            ],
          })
          .toPromise(),
      );
      isLicensed = licenses?.length === 1 && ConvertHelper.toBoolean(licenses[0]['IsAvailable']);
    } catch (err) {
      console.log(err); //console.log only, because of support for older WebApi
    }
    if (isLicensed) {
      return true;
    } else {
      return false;
    }
  }

  getOrders(id?: string): Observable<Order[]> {
    return this.userService.getCurrentLoginInfo().pipe(
      concatMap(async (loginInfo) => {
        const query = await this.createOrdersQuery(loginInfo, id);
        var data = ConvertHelper.arrayBufferToObjectArray(
          await this.coreDataService.executeReadObjectsQuery(query, false).toPromise(),
        );
        return data.map((obj) => new Order(obj));
      }),
    );
  }

  getIndexedMtPositionTypes(refreshCache?: boolean): Observable<{}> {
    return this.executeResultTypeQueries(refreshCache).pipe(
      map((queryResults) => {
        const indexedPosTypes = this.castAndIndexToArray(queryResults[0], 'Id', (val) => new MtPositionType(val));
        const indexedResultPosTypes = this.castAndIndexToArray(
          queryResults[1],
          'ParentId',
          (val) => new MtResultPosType(val),
        );
        const indexedResultTypes = this.castAndIndexToArray(queryResults[2], 'Id', (val) => {
          val.IsDescriptionRequired = !!val.IsDescriptionRequired; //This conversion is required for postgres DB
          val.IsPhotoRequired = !!val.IsPhotoRequired;
          val.IsQuantityRequired = !!val.IsQuantityRequired;

          return new MtResultType(val);
        });
        if (indexedPosTypes && !CompareHelper.isEmptyObject(indexedResultPosTypes)) {
          for (const groupedPosTypeId in indexedPosTypes) {
            if (indexedPosTypes.hasOwnProperty(groupedPosTypeId)) {
              const posType: MtPositionType = indexedPosTypes[groupedPosTypeId][0];
              if (indexedResultPosTypes[groupedPosTypeId]) {
                for (const resultPosType of indexedResultPosTypes[groupedPosTypeId]) {
                  const resultType: MtResultType = indexedResultTypes[resultPosType?.ResultTypeId][0];
                  if (resultPosType.IsSelectedByDefault) {
                    posType.DefaultResultTypes.push(resultType);
                  }
                  if (resultPosType.IsSelectedByAutoComplete) {
                    posType.AutoCompleteResultTypes.push(resultType);
                  }
                  if (resultPosType.SortOrder) {
                    posType.SortOrders.push({ type: resultType, index: resultPosType.SortOrder });
                  }
                  if (resultPosType.Designation) {
                    posType.Designations.push({ type: resultType, designation: resultPosType.Designation });
                  }
                  if (resultPosType.IdRefMtTaskTypeFollowing) {
                    posType.FollowingTaskTypeIds.push({
                      type: resultType,
                      taskTypeId: resultPosType.IdRefMtTaskTypeFollowing,
                    });
                  }
                  if (resultPosType.InvisibleExpression) {
                    posType.InvisibleExpressions.push({
                      type: resultType,
                      expression: resultPosType.InvisibleExpression,
                    });
                  }
                  posType.ResultTypes?.push(resultType);
                }
              }
            }
          }
        }
        return indexedPosTypes;
      }),
    );
  }

  getReadListDataSourceResult(
    objectType: string,
    columnName: string,
    refreshCache?: boolean,
  ): Observable<ReadListDataSourceResult> {
    const readListDataSourceModel = new ReadListDataSourceModel();
    readListDataSourceModel.ObjectKey = {
      ObjectType: objectType,
      Id: null,
    };
    readListDataSourceModel.ColumnName = columnName;
    return this.coreDataService
      .executeReadListDataSource(readListDataSourceModel, false, refreshCache)
      .pipe(map((data) => (!CompareHelper.isEmptyObject(data) ? (data as ReadListDataSourceResult) : null)));
  }

  private executeResultTypeQueries(refreshCache: boolean): Observable<ArrayBuffer[]> {
    return from(this.createResultTypeQueriesAsync()).pipe(
      concatMap((queries) => {
        const arr: Observable<ArrayBuffer>[] = [];
        for (const query of queries) {
          arr.push(this.coreDataService.executeReadObjectsQuery(query, false, refreshCache));
        }
        return forkJoin(arr);
      }),
    );
  }

  private async createResultTypeQueriesAsync(): Promise<QueryObjectsModel[]> {
    return [
      await this.createMtPositionTypeQueryAsync(),
      await this.createMtResultPosTypeQueryAsync(),
      await this.createMtResultTypeQueryAsync(),
    ];
  }

  private castAndIndexToArray(data: ArrayBuffer, field: string, factory) {
    const result = {};
    if (data) {
      const arrData: object[] = data as any;
      for (let i = 0; i < arrData.length; i++) {
        const fieldVal = arrData[i][field];
        const valArray = result[fieldVal];
        if (valArray) {
          valArray.push(factory(arrData[i]));
        } else {
          result[fieldVal] = [factory(arrData[i])];
        }
      }
    }
    return result;
  }

  private async createMtObjectsQueryAsync(workIds?: string[], indexedMtPositionTypes?: {}): Promise<QueryObjectsModel> {
    const queryDocument: QueryObjectsModel = new QueryObjectsModel();
    queryDocument.ObjectSpaceName = OperaContext.objectSpaceName;

    let worksOPath: string;
    if (workIds?.length) {
      let idList: string = undefined;
      for (const wId of workIds) {
        if (idList) {
          idList += `,'` + wId + `'`;
        } else {
          idList = `'` + wId + `'`;
        }
      }
      worksOPath = `Works[AuftragsId=$OrderId And Id In (` + idList + `)]`;
    } else {
      worksOPath = 'Works[AuftragsId=$OrderId]';
    }

    const mtObjectsQuery: Query = new Query();
    mtObjectsQuery.ObjectType = 'MtObject';
    mtObjectsQuery.OPath = 'Exists(' + worksOPath + ')';

    mtObjectsQuery.Columns = [
      this.coreDataService.createQueryColumn('Id'),
      this.coreDataService.createQueryColumn('ObjectType'),
      this.coreDataService.createQueryColumn('Designation'),
      this.coreDataService.createQueryColumn('GeObjectId'),
      this.coreDataService.createQueryColumn('GeObjectType'),
      this.coreDataService.createQueryColumn('ref:Typ', 'Type'),
      this.coreDataService.createQueryColumn('ref:IdRefStrasse', 'Street'),
      this.coreDataService.createQueryColumn(`${DataService.COMMENT_NOTE_OPATH}`, 'Note'),
    ];

    if (indexedMtPositionTypes) {
      for (const positionTypeId in indexedMtPositionTypes) {
        const posType: MtPositionType = indexedMtPositionTypes[positionTypeId][0];
        if (posType?.InvisibleExpressions?.length) {
          for (const exp of posType?.InvisibleExpressions) {
            mtObjectsQuery.Columns.push(
              this.coreDataService.createQueryColumn(
                `Id In (${exp.expression}.Id) Or IdMasterObject In (${exp.expression}.Id) And ParentId=$CurrentMunicipalityVariantId`,
                'InvisibleValues/' + positionTypeId + exp.type.Id,
              ),
            );
          }
        }
      }
    }

    const mtWorksQuery: Query = new Query();
    mtWorksQuery.OPath = worksOPath;
    mtWorksQuery.Name = 'Works';
    const allowDeletionExpression: string = this.getWorkAllowDeletionOpathExpression();
    mtWorksQuery.Columns = [
      this.coreDataService.createQueryColumn('Id'),
      this.coreDataService.createQueryColumn('Designation'),
      this.coreDataService.createQueryColumn('SortOrder'),
      this.coreDataService.createQueryColumn('State'),
      this.coreDataService.createQueryColumn('ref:State', 'StateDisplayText'),
      this.coreDataService.createQueryColumn('ExecutionDate'),
      this.coreDataService.createQueryColumn('ScheduledExecutionDate'),
      this.coreDataService.createQueryColumn(allowDeletionExpression, 'AllowDeletion'),
      this.coreDataService.createQueryColumn(
        "CaseNull(ToInt(WorkType.Properties[Designation='DurationMode'].TextValue), 0)",
        'WorkTypeDurationMode',
      ),
      this.coreDataService.createQueryColumn('Duration'),
    ];

    if ((await this.userService.getServerVersion())?.substring(0, 6) >= '9.26.1') {
      mtWorksQuery.Columns.push(
        this.coreDataService.createQueryColumn('CaseNull(ToInt(WorkType.DurationMode), 0)', 'WorkTypeDurationMode'),
      );
    } else {
      mtWorksQuery.Columns.push(
        this.coreDataService.createQueryColumn(
          "CaseNull(ToInt(WorkType.Properties[Designation='DurationMode'].TextValue), 0)",
          'WorkTypeDurationMode',
        ),
      );
    }

    const mtTasksQuery: Query = new Query();
    mtTasksQuery.OPath = 'Tasks';
    mtTasksQuery.Name = 'Tasks';
    mtTasksQuery.Columns = [
      this.coreDataService.createQueryColumn('Id'),
      this.coreDataService.createQueryColumn('Designation'),
      this.coreDataService.createQueryColumn('Description'),
      this.coreDataService.createQueryColumn('SortOrder'),
      this.coreDataService.createQueryColumn('AllowMultiSelelectResults', 'AllowMultiSelectResults'),
      this.coreDataService.createQueryColumn('AllowSelectResultsFromSingleCategory'),
      this.coreDataService.createQueryColumn('ResultsRequired'),
      this.coreDataService.createQueryColumn('Finished'),
      this.coreDataService.createQueryColumn('PositionTypeId'),
      this.coreDataService.createQueryColumn('TaskTypeId'),
      this.coreDataService.createQueryColumn('RelevantForEdges'),
      this.coreDataService.createQueryColumn('RelevantForStartNode'),
      this.coreDataService.createQueryColumn('RelevantForEndNode'),
      this.coreDataService.createQueryColumn('RelevantForMidNodes'),
      this.coreDataService.createQueryColumn('Geometry__Json', 'Geometry'),
      this.coreDataService.createQueryColumn('TaskType.AllowPointGeometries', 'AllowPointGeometries'),
      this.coreDataService.createQueryColumn('TaskType.AllowLineGeometries', 'AllowLineGeometries'),
      this.coreDataService.createQueryColumn('TaskType.AllowPolygonGeometries', 'AllowPolygonGeometries'),
    ];

    const mtResultsQuery: Query = new Query();
    mtResultsQuery.OPath = 'Results';
    mtResultsQuery.Name = 'Results';
    mtResultsQuery.Columns = [
      this.coreDataService.createQueryColumn('Id'),
      this.coreDataService.createQueryColumn('ResultTypeId'),
      this.coreDataService.createQueryColumn('Designation'),
      this.coreDataService.createQueryColumn('Quantity'),
      this.coreDataService.createQueryColumn('Description'),
      this.coreDataService.createQueryColumn('Time'),
      this.coreDataService.createQueryColumn('Date'),
      this.coreDataService.createQueryColumn(DataService.COMMENT_NO_PHOTO_IS_POSSIBLE_OPATH, 'PhotoImpossibleText'),
    ];

    const mtDocumentsQuery: Query = new Query();
    mtDocumentsQuery.OPath = 'Documents';
    mtDocumentsQuery.Name = 'Documents';
    mtDocumentsQuery.Columns = [
      this.coreDataService.createQueryColumn('Id'),
      this.coreDataService.createQueryColumn('Designation'),
      this.coreDataService.createQueryColumn('Kind'),
      this.coreDataService.createQueryColumn('Date'),
      this.coreDataService.createQueryColumn('GetDateTime(SysDateInsert, SysTimeInsert)', 'InsertAt'),
    ];

    mtResultsQuery.ObjectQueries = [mtDocumentsQuery];
    mtTasksQuery.ObjectQueries = [mtResultsQuery];
    mtWorksQuery.ObjectQueries = [mtTasksQuery];
    mtObjectsQuery.ObjectQueries = [mtWorksQuery];
    queryDocument.ObjectQueries = [mtObjectsQuery];
    queryDocument.Parameters = [new ParameterValue('OrderId', await this.getOrderId())];

    return queryDocument;
  }

  private getWorkAllowDeletionOpathExpression(): string {
    return "Source='1' And SysUserNameInsert = CaseNull(CurrentSystemUserName(), CurrentOSUsername())";
  }

  private getTicketAllowDeletionOpathExpression(): string {
    return "Herkunft='2' And SysUserNameInsert = CaseNull(CurrentSystemUserName(), CurrentOSUsername())";
  }

  getCurrentLoginInfo(): Promise<LoginInfo> {
    return this.userService.getCurrentLoginInfo().toPromise();
  }

  private async createMtResourceTypesQuery(): Promise<QueryObjectsModel> {
    const queryDocument: QueryObjectsModel = new QueryObjectsModel();
    queryDocument.ObjectSpaceName = OperaContext.objectSpaceName;

    const mtResourceTypeQuery: Query = new Query();
    mtResourceTypeQuery.ObjectType = 'MtResourceType';
    mtResourceTypeQuery.OPath = 'Exists(Resources[Type=1].ResourceOrders[ParentId=$OrderId])';

    mtResourceTypeQuery.Columns = [
      this.coreDataService.createQueryColumn('Name'),
      this.coreDataService.createQueryColumn('Kind'),
      this.coreDataService.createQueryColumn('DefaultUnit'),
    ];

    const mtResourcesQuery: Query = new Query();
    mtResourcesQuery.OPath = 'Resources[Type=1 And Exists(ResourceOrders[ParentId=$OrderId])]';
    mtResourcesQuery.Name = 'Resources';

    mtResourcesQuery.Columns = [
      this.coreDataService.createQueryColumn('Id'),
      this.coreDataService.createQueryColumn('Name'),
      this.coreDataService.createQueryColumn('Login'),
      this.coreDataService.createQueryColumn('DefaultUnit'),
    ];

    const mtResourceEventQuery: Query = new Query();
    //Spec from FIN 20.03.24: Not IsNull(IdRefResourceEventType)
    mtResourceEventQuery.OPath = 'ResourceEvents [Not IsNull(IdRefResourceEventType) And IdRefAuftrag=$OrderId]';
    mtResourceEventQuery.Name = 'ResourceEvents';

    mtResourceEventQuery.Columns = [
      this.coreDataService.createQueryColumn('Id'),
      this.coreDataService.createQueryColumn('Index'),
      this.coreDataService.createQueryColumn('Name'),
      this.coreDataService.createQueryColumn('Code'),
      this.coreDataService.createQueryColumn('IdRefResourceEventType'),
      this.coreDataService.createQueryColumn('Date'),
      this.coreDataService.createQueryColumn('EndDate'),
      this.coreDataService.createQueryColumn('Time'),
      this.coreDataService.createQueryColumn('EndTime'),
      this.coreDataService.createQueryColumn('TimespanOff'),
      this.coreDataService.createQueryColumn('Value'),
      this.coreDataService.createQueryColumn('EndValue'),
      this.coreDataService.createQueryColumn('ResultValue'),
      this.coreDataService.createQueryColumn('Unit'),
      this.coreDataService.createQueryColumn("Comments[SeqNr=1 And Identifier='Standard'].Text", 'Comment'), //According to spec, only the first comment
    ];

    const mtResourceEventTypesQuery: Query = new Query();
    mtResourceEventTypesQuery.OPath = 'ResourceEventTypes';
    mtResourceEventTypesQuery.Name = 'ResourceEventTypes';

    mtResourceEventTypesQuery.Columns = [
      this.coreDataService.createQueryColumn('Id'),
      this.coreDataService.createQueryColumn('Kind'),
      this.coreDataService.createQueryColumn('ref:Kind', 'KindName'),
      this.coreDataService.createQueryColumn('Name'),
      this.coreDataService.createQueryColumn('Code'),
      this.coreDataService.createQueryColumn('DefaultUnit'),
    ];

    mtResourcesQuery.ObjectQueries = [mtResourceEventQuery];
    mtResourceTypeQuery.ObjectQueries = [mtResourcesQuery, mtResourceEventTypesQuery];
    queryDocument.ObjectQueries = [mtResourceTypeQuery];
    queryDocument.Parameters = [new ParameterValue('OrderId', await this.getOrderId())];

    return queryDocument;
  }

  private async createMtWorkTypesQuery(): Promise<QueryObjectsModel> {
    return WorkService.createWorkTypeQuery(await this.getWorkTypeFilter(ImWorkService.AdditionalWorkTypeFilter));
  }

  private async createRemindersQueryAsync(): Promise<QueryObjectsModel> {
    const queryDocument: QueryObjectsModel = new QueryObjectsModel();
    queryDocument.ObjectSpaceName = OperaContext.objectSpaceName;

    const remindersQuery: Query = new Query();
    remindersQuery.ObjectType = 'Reminder';

    remindersQuery.Columns = [
      this.coreDataService.createQueryColumn('Id'),
      this.coreDataService.createQueryColumn('ParentId'),
      this.coreDataService.createQueryColumn('IsActive'),
    ];

    queryDocument.ObjectQueries = [remindersQuery];
    return queryDocument;
  }

  private async createMtTicketsQueryAsync(): Promise<QueryObjectsModel> {
    const queryDocument: QueryObjectsModel = new QueryObjectsModel();
    queryDocument.ObjectSpaceName = OperaContext.objectSpaceName;

    const mtTicketsQuery: Query = new Query();
    mtTicketsQuery.ObjectType = 'MtTicket';

    let ticketOpath = `ParentId=$CurrentMunicipalityVariantId`;
    const loginInfo: LoginInfo = await this.getCurrentLoginInfo();
    const tasksUserFilter: string =
      'TicketTasks[Exists(Resource' + (await this.getResourceUserFilter(loginInfo)) + ')]';

    ticketOpath += ' And Exists(' + tasksUserFilter + ')';
    queryDocument.Parameters = [
      new ParameterValue('UserName', loginInfo.displayName),
      new ParameterValue('UserLogin', loginInfo.userName),
    ];

    mtTicketsQuery.OPath = ticketOpath;

    const allowDeletionExpression: string = this.getTicketAllowDeletionOpathExpression();
    mtTicketsQuery.Columns = [
      this.coreDataService.createQueryColumn('Id'),
      this.coreDataService.createQueryColumn('Nummer', 'Nummer', QueryColumnSortOrder.Ascending),
      this.coreDataService.createQueryColumn('Dringlichkeit'),
      this.coreDataService.createQueryColumn('ref:Dringlichkeit', 'DringlichkeitDisplayText'),
      this.coreDataService.createQueryColumn('Bezeichnung'),
      this.coreDataService.createQueryColumn('Status'),
      this.coreDataService.createQueryColumn(
        'Exists(GeometryPoints) Or Exists(GeometryLines) Or Exists(GeometryPolygons)',
        'HasGeometry',
      ),
      this.coreDataService.createQueryColumn(allowDeletionExpression, 'AllowDeletion'),
    ];

    const mtTicketTasksQuery: Query = new Query();
    mtTicketTasksQuery.OPath = tasksUserFilter;

    mtTicketTasksQuery.Name = 'TicketTasks';
    mtTicketTasksQuery.Columns = [
      this.coreDataService.createQueryColumn('Id'),
      this.coreDataService.createQueryColumn('ObjectType'),
      this.coreDataService.createQueryColumn('Bezeichnung', 'Bezeichnung', QueryColumnSortOrder.Ascending),
      this.coreDataService.createQueryColumn('Status'),
      this.coreDataService.createQueryColumn('ref:Status', 'StatusDisplayText'),
      this.coreDataService.createQueryColumn('StartDatum'),
      this.coreDataService.createQueryColumn('StartZeit'),
      this.coreDataService.createQueryColumn('EndDatum'),
      this.coreDataService.createQueryColumn('EndZeit'),
      this.coreDataService.createQueryColumn('IdRefMtResource'),
    ];

    const remindersQuery: Query = new Query();
    remindersQuery.OPath = 'Reminders';
    remindersQuery.Name = 'Reminders';
    remindersQuery.Columns = [
      this.coreDataService.createQueryColumn('Id'),
      this.coreDataService.createQueryColumn('IsActive'),
    ];

    const mtDocumentsQuery: Query = new Query();
    mtDocumentsQuery.OPath = 'Documents';
    mtDocumentsQuery.Name = 'Documents';
    mtDocumentsQuery.Columns = [
      this.coreDataService.createQueryColumn('Id'),
      this.coreDataService.createQueryColumn('Designation'),
      this.coreDataService.createQueryColumn('Kind'),
      this.coreDataService.createQueryColumn('Date'),
      this.coreDataService.createQueryColumn('GetDateTime(SysDateInsert, SysTimeInsert)', 'InsertAt'),
    ];

    const commentsQuery: Query = new Query();
    commentsQuery.OPath = 'Comments';
    commentsQuery.Name = 'Comments';
    commentsQuery.Columns = CommentModel.createQueryColumns();

    const mtObjectsQuery: Query = new Query();
    mtObjectsQuery.ObjectType = 'MtObject';
    mtObjectsQuery.OPath = `Object`;
    mtObjectsQuery.Name = 'Objects'; //to reference, handle at the contructor

    mtObjectsQuery.Columns = [
      this.coreDataService.createQueryColumn('Id'),
      this.coreDataService.createQueryColumn('ObjectType'),
      this.coreDataService.createQueryColumn('Designation'),
      this.coreDataService.createQueryColumn('GeObjectId'),
      this.coreDataService.createQueryColumn('GeObjectType'),
      this.coreDataService.createQueryColumn('ref:Typ', 'Type'),
      this.coreDataService.createQueryColumn('ref:IdRefStrasse', 'Street'),
    ];

    mtTicketTasksQuery.ObjectQueries = [remindersQuery, mtDocumentsQuery, commentsQuery];
    mtTicketsQuery.ObjectQueries = [mtTicketTasksQuery, mtObjectsQuery];
    queryDocument.ObjectQueries = [mtTicketsQuery];
    return queryDocument;
  }

  private async createMtTicketResourcesQueryAsync(): Promise<QueryObjectsModel> {
    const queryDocument: QueryObjectsModel = new QueryObjectsModel();
    queryDocument.ObjectSpaceName = OperaContext.objectSpaceName;

    const mtResourcesQuery: Query = new Query();
    mtResourcesQuery.ObjectType = 'MtResource';
    mtResourcesQuery.OPath = 'Type=1 And Not IsNull(Login)';

    mtResourcesQuery.Columns = [
      this.coreDataService.createQueryColumn('Id'),
      this.coreDataService.createQueryColumn('Name'),
      this.coreDataService.createQueryColumn('Login'),
    ];
    queryDocument.ObjectQueries = [mtResourcesQuery];
    return queryDocument;
  }
  private async createMtTicketsCountQueryAsync(): Promise<QueryObjectsModel> {
    const loginInfo: LoginInfo = await this.getCurrentLoginInfo();
    return this.createMtTicketsCountQueryByUserFilterAsync(loginInfo);
  }

  private async createMtTicketsCountQueryByUserFilterAsync(loginInfo: LoginInfo): Promise<QueryObjectsModel> {
    const queryDocument: QueryObjectsModel = new QueryObjectsModel();
    queryDocument.ObjectSpaceName = OperaContext.objectSpaceName;

    const mtTicketsQuery: Query = new Query();
    mtTicketsQuery.ObjectType = 'GemeindeVariante';
    mtTicketsQuery.OPath = `Id=$CurrentMunicipalityVariantId`;
    const resourceUserFilter = await this.getResourceUserFilter(loginInfo);
    const colAllTicketsCount = this.coreDataService.createQueryColumn(
      'Count(Type(MtTicket)[ParentId= ^.Id And Exists(TicketTasks.Resource' + resourceUserFilter + ')])',
      DataService.COL_CountTicketsAll,
    );
    const resourceExistsFilter = 'Exists(Resource' + resourceUserFilter + ')';
    const colCountTicketsFinished = this.coreDataService.createQueryColumn(
      'Count(Type(MtTicket)[ParentId=^.Id And Count(TicketTasks[' +
        resourceExistsFilter +
        "])>0 And Count(TicketTasks[Status='2' And " +
        resourceExistsFilter +
        '])=Count(TicketTasks[' +
        resourceExistsFilter +
        '])])',
      DataService.COL_CountTicketsFinished,
    );
    const colCountTicketsEditing = this.coreDataService.createQueryColumn(
      "Count(Type(MtTicket)[ParentId=^.Id And  ((Count(TicketTasks[Status='2' And " +
        resourceExistsFilter +
        "])> 0 And Count(TicketTasks[Status='2' And " +
        resourceExistsFilter +
        '])<Count(TicketTasks[' +
        resourceExistsFilter +
        "])) Or Count(TicketTasks[Status='1' And " +
        resourceExistsFilter +
        '])>0)])',
      DataService.COL_CountTicketsEditing,
    );

    const colCountTicketsNotStarted = this.coreDataService.createQueryColumn(
      'Count(Type(MtTicket)[ParentId=^.Id And (Count(TicketTasks[' +
        resourceExistsFilter +
        "])> 0 And Count(TicketTasks[Status='0' And " +
        resourceExistsFilter +
        ']) = Count(TicketTasks[' +
        resourceExistsFilter +
        ']))])',
      DataService.COL_CountTicketsNotStarted,
    );

    const resourceFilterForReminder = await this.getResourceUserFilterForReminder(loginInfo);
    const colHasTicketTaskReminder = this.coreDataService.createQueryColumn(
      'Exists(Type(MtTicketTask)[Ticket.ParentId=^.Id And ' + resourceFilterForReminder + ' And Exists(Reminders)])',
      DataService.COL_HasTicketTaskReminders,
    );

    mtTicketsQuery.Columns = [
      colAllTicketsCount,
      colCountTicketsFinished,
      colCountTicketsEditing,
      colCountTicketsNotStarted,
      colHasTicketTaskReminder,
    ];

    queryDocument.Parameters = [
      new ParameterValue('UserName', loginInfo.displayName),
      new ParameterValue('UserLogin', loginInfo.userName),
    ];

    queryDocument.ObjectQueries = [mtTicketsQuery];
    return queryDocument;
  }

  private async createOrdersQuery(loginInfo: LoginInfo, id?: string): Promise<QueryObjectsModel> {
    const queryDocument: QueryObjectsModel = new QueryObjectsModel();
    queryDocument.ObjectSpaceName = OperaContext.objectSpaceName;
    console.log(OperaContext.objectSpaceName);

    const ordersQuery: Query = new Query();
    ordersQuery.ObjectType = 'Auftrag';
    if (!id) {
      OperaContext.getOrderOpath(await this.getResourceUserFilter(loginInfo));
      ordersQuery.OPath = OperaContext.getOrderOpath(await this.getResourceUserFilter(loginInfo));
      // if (this.actuelSystem === 'basys') {
      //   ordersQuery.OPath =
      //     `ParentId=$CurrentMunicipalityVariantId And (Not (CaseNull(Status,1) In (0,3)) Or CaseNull(Status, 0) = 0 And KkStatus In ('3','4','5'))` +
      //     ` And (Exists(Properties[Designation='ExportBCEDocId' And Not IsNull(TextValue)]) And Not Exists(MtResourceOrders) or Exists(MtResourceOrders.Resource` +
      //     (await this.getResourceUserFilter(loginInfo)) +
      //     `))`;
      // } else {
      //   ordersQuery.OPath =
      //     `(CaseNull(Status,1) In (0,3))` +
      //     ` And (Exists(Properties[Designation='ExportBCEDocId' And Not IsNull(TextValue)]) And Not Exists(MtResourceOrders) or Exists(MtResourceOrders.Resource` +
      //     (await this.getResourceUserFilter(loginInfo)) +
      //     `))`;
      // }
      queryDocument.Parameters = [
        new ParameterValue('UserName', loginInfo.displayName),
        new ParameterValue('UserLogin', loginInfo.userName),
      ];
    } else {
      ordersQuery.OPath = 'Id=$Id';
      queryDocument.Parameters = [new ParameterValue('Id', id)];
    }

    ordersQuery.Columns = [
      this.coreDataService.createQueryColumn('Id'),
      this.coreDataService.createQueryColumn('Bezeichnung', 'Designation'),
      this.coreDataService.createQueryColumn('Date'),
      this.coreDataService.createQueryColumn('Bemerkung', 'Remark'),
      this.coreDataService.createQueryColumn('Type(MtWork)[AuftragsId=^.Id].Count()', 'CountWorks'),
      this.coreDataService.createQueryColumn(
        'Type(MtWork)[AuftragsId=^.Id And Not(State In (3,4))].Count()',
        'CountWorksOpen',
      ),
      this.coreDataService.createQueryColumn('Type(MtWork)[AuftragsId=^.Id And State=3].Count()', 'CountWorksPartial'),
      this.coreDataService.createQueryColumn('Type(MtWork)[AuftragsId=^.Id And State=4].Count()', 'CountWorksDone'),
    ];
    queryDocument.ObjectQueries = [ordersQuery];

    return queryDocument;
  }

  private async getResourceUserFilter(loginInfo: LoginInfo): Promise<string> {
    const version = await this.userService.getServerVersion();
    if (loginInfo?.isAnonymous) {
      return '[Name=CurrentOSUserName() or Login=CurrentOSUserName() or IsCurrentOSUserInGroup(Login)]';
    } else {
      return '[Name=CaseNull($UserName, CurrentSystemUserName()) or Login=CaseNull($UserLogin, CurrentSystemUserName()) Or IsCurrentSystemUserInGroup(Login)]';
    }
  }

  private async getResourceUserFilterForReminder(loginInfo: LoginInfo): Promise<string> {
    if (loginInfo?.isAnonymous) {
      return '(Resource.Name=CurrentOSUserName() or Resource.Login=CurrentOSUserName() or IsCurrentOSUserInGroup($Login))';
    } else {
      return '(Resource.Name=CaseNull($UserName, CurrentSystemUserName()) or Resource.Login=CaseNull($UserLogin, CurrentSystemUserName()) Or IsCurrentSystemUserInGroup($Login))';
    }
  }

  private async getResourceUserFilterForResourceQuery(loginInfo: LoginInfo): Promise<string> {
    if (loginInfo?.isAnonymous) {
      return 'Name=CurrentOSUserName() or Login=CurrentOSUserName() or IsCurrentOSUserInGroup(Login)';
    } else {
      return 'Name=CaseNull($UserName, CurrentSystemUserName()) or Login=CaseNull($UserLogin, CurrentSystemUserName()) Or IsCurrentSystemUserInGroup(Login)';
    }
  }

  private async createMtPositionTypeQueryAsync() {
    const queryColumn = new Array<QueryColumn>();
    queryColumn.push({ Name: 'Id', OPath: 'Id' });
    return this.coreDataService.createSimpleQuery(
      OperaContext.objectSpaceName,
      'MtPositionType',
      `Exists(Tasks.MtWork[AuftragsId=$OrderId]) Or Exists(Type(MtWorkType)[${await this.getWorkTypeFilter(ImWorkService.AdditionalWorkTypeFilter)}].TaskTypes[PositionTypeId = ^.^.Id])`,
      queryColumn,
      [new ParameterValue('OrderId', await this.getOrderId())],
    );
  }

  private async createMtResultPosTypeQueryAsync() {
    const queryColumn = new Array<QueryColumn>();
    queryColumn.push(
      this.coreDataService.createQueryColumn('ParentId'),
      this.coreDataService.createQueryColumn('Designation'),
      this.coreDataService.createQueryColumn('ResultTypeId'),
      this.coreDataService.createQueryColumn('IsSelectedByDefault'),
      this.coreDataService.createQueryColumn(
        'CaseNull(IsSelectedByAutoComplete, IsSelectedByDefault)',
        'IsSelectedByAutoComplete',
      ),
      this.coreDataService.createQueryColumn('SortOrder'),
      this.coreDataService.createQueryColumn('IdRefMtTaskTypeFollowing'),
      this.coreDataService.createQueryColumn('InvisibleExpression'),
    );
    return this.coreDataService.createSimpleQuery(
      OperaContext.objectSpaceName,
      'MtResultPosType',
      `Exists(Type(MtTask)[PositionTypeId=^.ParentId And MtWork.AuftragsId=$OrderId]) Or Exists(Type(MtWorkType)[${await this.getWorkTypeFilter(ImWorkService.AdditionalWorkTypeFilter)}].TaskTypes[PositionTypeId = ^.^.ParentId])`,
      queryColumn,
      [new ParameterValue('OrderId', await this.getOrderId())],
    );
  }

  private async createMtResultTypeQueryAsync(): Promise<QueryObjectsModel> {
    const queryColumn = new Array<QueryColumn>();
    queryColumn.push(
      this.coreDataService.createQueryColumn('Id'),
      this.coreDataService.createQueryColumn('Designation'),
      this.coreDataService.createQueryColumn('Description'),
      this.coreDataService.createQueryColumn('Category'),
      this.coreDataService.createQueryColumn('SetTaskCompleteOnSelection'),
      this.coreDataService.createQueryColumn('Caption'),
      this.coreDataService.createQueryColumn('Unit'),
      this.coreDataService.createQueryColumn('UnitPrice'),
      this.coreDataService.createQueryColumn('Code'),
      this.coreDataService.createQueryColumn('IsQuantityRequired'),
      this.coreDataService.createQueryColumn('IsPhotoRequired'),
      this.coreDataService.createQueryColumn('IsDescriptionRequired'),
      this.coreDataService.createQueryColumn('IdRefMtTaskTypeFollowing'),
      this.coreDataService.createQueryColumn('IsGeometryRequired'),
    );

    return this.coreDataService.createSimpleQuery(
      OperaContext.objectSpaceName,
      'MtResultType',
      `Exists(Type(MtTask)[PositionTypeId in (^.PositionTypes.ParentId) And MtWork.AuftragsId=$OrderId]) Or Exists(Type(MtWorkType)[${await this.getWorkTypeFilter(ImWorkService.AdditionalWorkTypeFilter)}].TaskTypes[PositionTypeId in (^.^.PositionTypes.ParentId)])`,
      queryColumn,
      [new ParameterValue('OrderId', await this.getOrderId())],
    );
  }

  public dispose(): void {
    this.subscriptions.forEach((element) => {
      element.unsubscribe();
    });
  }

  public loadState(): Observable<{ current: any; last: any }> {
    return forkJoin(
      from(this.persistenceService.cacheAdapter.loadState(this.currentStateContext)),
      from(this.persistenceService.cacheAdapter.loadState(this.lastStateContext)),
    ).pipe(
      map((arr) => {
        return { current: arr[0], last: arr[1] };
      }),
    );
  }

  public queryPicklistItems(picklistName: string): Observable<any> {
    const observable$ = new Observable((observer) => {
      const queryPicklistItems: ReadPicklistItemsModel = new ReadPicklistItemsModel();
      queryPicklistItems.Indent = true;
      queryPicklistItems.OutputMode = 0;
      queryPicklistItems.Name = picklistName;
      this.coreDataService.executeReadPicklistsItemsQuery(queryPicklistItems).subscribe((result) => {
        observer.next(result);
      });
    });
    return observable$;
  }

  public async getCurrentUserResourceId(): Promise<string | undefined> {
    const login = await this.userService.getCurrentLoginInfo().toPromise();
    const query = await this.createResourceQueryForLogin(login);

    const objs: { id: any }[] = await this.coreDataService
      .executeReadObjectsQuery(query, false, false)
      .pipe(
        map((data) => (!CompareHelper.isEmptyObject(data) ? (data as any as object[]) : [])),
        map((data) => data.map((obj: any) => ({ id: obj.Id }))),
      )
      .toPromise();

    if (objs.length > 0) return objs[0].id;
    else {
      return undefined;
    }
  }

  private async createResourceQueryForLogin(loginInfo: LoginInfo): Promise<QueryObjectsModel> {
    const queryDocument: QueryObjectsModel = new QueryObjectsModel();
    queryDocument.ObjectSpaceName = OperaContext.objectSpaceName;

    const resourceQuery: Query = new Query();
    resourceQuery.ObjectType = 'MtResource';
    resourceQuery.OPath =
      '(ParentId = $CurrentMunicipalityId) And (' + (await this.getResourceUserFilterForResourceQuery(loginInfo)) + ')';

    queryDocument.Parameters = [
      new ParameterValue('UserName', loginInfo.displayName),
      new ParameterValue('UserLogin', loginInfo.userName),
    ];

    resourceQuery.Columns = [this.coreDataService.createQueryColumn('Id')];
    queryDocument.ObjectQueries = [resourceQuery];

    return queryDocument;
  }
  public async downloadDocumentThumb(docId: string): Promise<string | null> {
    const queryDocument = new QueryObjectsModel();
    queryDocument.ObjectSpaceName = OperaContext.objectSpaceName;

    const docQuery = new Query();
    docQuery.ObjectType = 'Document';
    docQuery.OPath = 'Id=$Id';
    docQuery.Columns = [
      this.coreDataService.createQueryColumn('Object', null, null, null, null, null, 'Base64Image[200x200]'),
    ];

    queryDocument.Parameters = [new ParameterValue('Id', docId)];

    queryDocument.ObjectQueries = [docQuery];

    const result = await this.coreDataService.executeReadObjectsQuery(queryDocument, false, false).toPromise();

    const rows = ConvertHelper.arrayBufferToObjectArray(result) as any[];
    return rows?.length ? (rows[0]?.Object ?? null) : null;
  }

  public async sendNotification(userOrGroupName: string, title: string, body: string): Promise<void> {
    const model: SendNotificationModel = new SendNotificationModel();
    model.UserOrGroupNames = [userOrGroupName];

    const data: NotificationData = new NotificationData();
    data.title = title;
    data.body = body;

    model.Data.data = data;

    await this.coreDataService.sendNotification(model).toPromise();
  }

  public async getWorkTypeFilter(additionalFilter?: string): Promise<string> {
    return DataService.getWorkTypeFilterCore(this.userService, additionalFilter);
  }

  public static async getWorkTypeFilterCore(userService: UserService, additionalFilter?: string): Promise<string> {
    let filter = 'ParentId = $CurrentMunicipalityId';
    if ((await userService.getServerVersion())?.substring(0, 6) >= '9.26.1') {
      filter += " And State <> '1'";
    }
    if (additionalFilter) {
      filter += ' And (' + additionalFilter + ')';
    }
    return filter;
  }
}
